import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Ambience, Mono, Reveal } from "@/components/network/NetworkAtoms";
import { PrimaryCta, SecondaryCta } from "./FundingCta";
import PhoneApproval from "./PhoneApproval";
import { Clock, BadgePoundSterling, UserRound } from "lucide-react";

const ICONS = [Clock, BadgePoundSterling, UserRound];

const GENERAL = {
  eyebrow: "For UK businesses",
  h1: ["One application.", "The funders who fit it.", "Nothing to pay us."],
  lede: "Connexis is the desk between your business and the market. You put the case once. We place it with the "
      + "funders on our panel whose criteria it actually meets — any sector, any purpose, £25,000 to £500m and "
      + "beyond — and we run it to terms you can read.",
  proof: [
    { label: "Out to funders", value: "Same day" },
    { label: "Facility range", value: "£25k – £500m+" },
    { label: "Cost to you", value: "Nothing" },
  ],
  types: ["Term debt", "Asset finance", "Invoice finance", "Property & bridging", "Development finance",
          "Commercial mortgages", "Acquisition & structured debt", "VAT & tax"],
};

const jumpToProducts = () => document.querySelector('[data-testid="funding-products"]')
  ?.scrollIntoView({ behavior: "smooth", block: "start" });

export default function ApplicantHero({ sector }) {
  const reduce = useReducedMotion();
  const c = sector || GENERAL;
  const [l1, l2, l3] = c.h1;

  return (
    <section className="relative overflow-hidden" data-testid="funding-hero">
      <Ambience />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-16 sm:pt-24 pb-16 sm:pb-24">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-14 lg:gap-10 items-center">
          <div className="max-w-xl">
            <motion.div initial={reduce ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }} className="inline-flex items-center gap-2.5">
              <span className="h-px w-7 bg-[#7da9ff]/50" />
              <Mono>{c.eyebrow}</Mono>
            </motion.div>

            <motion.h1 initial={reduce ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                       transition={{ duration: 0.8, delay: 0.08 }}
                       className="mt-6 font-cabinet text-[38px] sm:text-[52px] lg:text-[58px] leading-[1.02] tracking-[-0.035em] font-bold text-white">
              {l1}
              <br className="hidden sm:block" /> {l2}
              <br className="hidden sm:block" /> <span className="text-[#7da9ff]">{l3}</span>
            </motion.h1>

            <motion.p initial={reduce ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.16 }}
                      className="mt-6 text-[16px] sm:text-[17px] leading-relaxed text-slate-300">
              {c.lede}
            </motion.p>

            <motion.div initial={reduce ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }} className="mt-7">
              <div className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500">
                {sector ? `What we place in ${sector.short.toLowerCase()}` : "What we arrange"} · {c.proof[1].value}
              </div>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="funding-hero-types">
                {c.types.map((t) => (
                  <button key={t} onClick={jumpToProducts}
                          className="rounded-full border border-white/12 bg-white/[0.03] px-3 py-1.5 text-[12.5px] text-slate-300 hover:border-[#0055FF]/50 hover:text-white transition-colors">
                    {t}
                  </button>
                ))}
              </div>
            </motion.div>

            <motion.div initial={reduce ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.24 }}
                        className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <PrimaryCta testId="funding-hero-primary" />
              <SecondaryCta testId="funding-hero-desk" />
            </motion.div>

            <motion.dl initial={reduce ? false : { opacity: 0 }} animate={{ opacity: 1 }}
                       transition={{ duration: 0.9, delay: 0.36 }}
                       className="mt-12 border-t border-white/10 divide-y divide-white/[0.07] sm:divide-y-0 sm:grid sm:grid-cols-3 sm:gap-6 sm:pt-7"
                       data-testid="funding-hero-proof">
              {c.proof.map(({ label, value }, i) => {
                const Icon = ICONS[i];
                return (
                  <div key={label}
                       className="flex items-center justify-between gap-4 py-4 sm:flex-col sm:items-stretch sm:gap-0 sm:py-0">
                    <div className="flex items-center gap-2.5 min-w-0 sm:block">
                      <Icon className="size-4 shrink-0 text-[#7da9ff]" />
                      <dt className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 sm:mt-2.5 sm:text-balance">{label}</dt>
                    </div>
                    <dd className="shrink-0 text-right text-[15px] font-semibold tracking-tight text-white sm:mt-auto sm:pt-1 sm:text-left sm:text-[17px]">
                      {value}
                    </dd>
                  </div>
                );
              })}
            </motion.dl>
          </div>

          <Reveal delay={0.15}>
            <PhoneApproval />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
